import type { AutocompleteChangeReason } from "@mui/material/Autocomplete";
import Checkbox from "@mui/material/Checkbox";
import MenuItem from "@mui/material/MenuItem";
import type { Color } from "common/utilities/colors/colors.types";
import { arrayObjFieldsUnique } from "common/utilities/core/array";
import { getTextWidth } from "common/utilities/core/string";
import theme, { createStyles } from "core/utilities/theme";
import type React from "react";
import { useRef } from "react";

// Note that we need to work with a callback instead of a promise
// to avoid rendering issues when debouncing requests
export type RemoteOptionsCallback = (options: OptionType[]) => void;
export type RemoteOptions = (inputValue: string, callback: RemoteOptionsCallback) => void;

// Array of options that need to be passed to the
// component as options and values
export interface OptionType<T = unknown> {
  label: string;
  value: string;
  refId?: string;
  isDisabled?: boolean;
  data?: T;
  color?: Color;
  backgroundColor?: string;
  separator?: boolean;
}

export interface OptionTypeData {
  toFilterOut: boolean;
}

export interface MenuPosition {
  menuPlacement: "bottom" | "top";
  maxHeight: number;
}

export type OnChangeValue = (value?: OptionType[]) => void;
export interface SelectOptionTypeRange {
  from: OptionType<unknown>;
  to: OptionType<unknown>;
  action: Extract<AutocompleteChangeReason, "selectOption" | "removeOption">;
}

const handleMultipleValueChange = (
  onChange: OnChangeValue,
  values: OptionType<unknown>[],
  options: OptionType<unknown>[],
  selectRange?: SelectOptionTypeRange
) => {
  let shiftValues: OptionType<unknown>[] = [];

  let newValues = [...values];

  if (selectRange) {
    // Pick the value before last to start the range selection.
    // Ensure it is in the current list of options.
    if (options.some((o) => o.value === selectRange.from.value)) {
      const fromValueIndex = options.findIndex((o) => o.value === selectRange.from.value);
      const toValueIndex = options.findIndex((o) => o.value === selectRange.to.value);
      shiftValues = options.slice(Math.min(fromValueIndex, toValueIndex), Math.max(fromValueIndex, toValueIndex));

      if (selectRange.action === "selectOption") {
        newValues = arrayObjFieldsUnique([...newValues, ...shiftValues], (item) => item.refId || item.value);
      }

      if (selectRange.action === "removeOption") {
        const shiftValuesValues = shiftValues.map((v) => v.refId || v.value);
        newValues = newValues.filter((v) => !shiftValuesValues.includes(v.refId || v.value));
      }
    }
  }

  onChange(newValues);
};

export const handleChange = (
  onChange: OnChangeValue,
  value: OptionType<unknown> | OptionType<unknown>[] | null,
  options: OptionType<unknown>[],
  selectRange?: SelectOptionTypeRange
) => {
  if (value) {
    if (Array.isArray(value)) {
      handleMultipleValueChange(onChange, Array.from(value), options, selectRange);
    } else {
      onChange([value] as OptionType[]);
    }
  } else {
    onChange();
  }
};

export const filterOutOptions = (opts: OptionType<unknown>[]) =>
  opts.filter((o) => !(o.data as OptionTypeData)?.toFilterOut);

export const classes = createStyles({
  noOptions: {
    fontSize: (t) => t.typography.body2.fontSize,
    whiteSpace: "nowrap",
  },
  loading: {
    fontSize: (t) => t.typography.body2.fontSize,
  },
  option: {
    fontSize: (t) => t.typography.body2.fontSize,
  },
  inputRoot: {
    paddingRight: "0 !important",
  },
});

export const isOptionValue = (o: OptionType, v: OptionType) =>
  o.refId && v.refId ? o.refId === v.refId : o.value === v.value;

export const isOptionWithinValues = (o: OptionType, values: OptionType[]) => values.some((v) => isOptionValue(o, v));

/**
 * Disable options and edit label that have been already selected (values)
 */
export const markOptionsWithinValuesAsSelected = (
  isMulti: boolean | undefined,
  options: OptionType[],
  values: OptionType[]
) =>
  options.map((o) => {
    const isSelected = isOptionWithinValues(o, values);

    return {
      ...o,
      isDisabled: isSelected && !isMulti ? true : o.isDisabled,
      label: isSelected && !isMulti ? `${o.label} (selected)` : o.label,
    } as OptionType;
  });

/**
 * Add data attribute and styling to option
 * Default: https://github.com/mui/material-ui/blob/master/packages/mui-material/src/Autocomplete/Autocomplete.js#L511
 */
export const renderOption = (
  isMulti: boolean | undefined,
  props: React.HTMLAttributes<HTMLLIElement>,
  option: OptionType | string
) => (
  <MenuItem
    {...props}
    dense
    data-e2e-form-advanced-selection-option=""
    divider={typeof option !== "string" && option.separator}
  >
    {isMulti && (
      <Checkbox
        size="small"
        checked={props["aria-selected"] === true}
        sx={{ margin: theme.spacing(0, 1, 0, 0), padding: 0 }}
      />
    )}
    {typeof option === "string" && option}
    {typeof option !== "string" && option.label.length === 0 && <>&#8203;</>}
    {typeof option !== "string" && option.label.length > 0 && option.label}
  </MenuItem>
);

export function useDropdownScrollPosition() {
  const lastScrollPosition = useRef<number | undefined>();

  const storeScrollPosition = () => {
    lastScrollPosition.current = document.querySelector(".MuiAutocomplete-popper ul")?.scrollTop;
  };

  return {
    dropdownScrollPosition: lastScrollPosition.current,
    storeDropdownScrollPosition: storeScrollPosition,
  };
}

const optionFont = ["400", "1rem", theme.typography.fontFamily as string].join(" ");

export const calcLongestOptionLabelWidth = (options: OptionType[]) =>
  options.length > 0 ? Math.max(...options.map((o) => getTextWidth(o.label, optionFont, "0.00938em"))) : 0;
